import { EnrichButton } from '@aibees/elephant/components';
import { FindButton } from '@aibees/elephant/components/find-button';
import { SaveDialog } from '@aibees/elephant/components/save-dialog';
import { SearchTableActionDialogLists } from '@aibees/elephant/components/search-table-action-dialog-lists';
import { SearchTableActionDialogMore } from '@aibees/elephant/components/search-table-action-dialog-more';
import { SearchTableHeaderActionButton } from '@aibees/elephant/components/search-table-header-action-button';
import { useTableActionContext } from '@aibees/elephant/context';
import { useSearchTableFromRoute } from '@aibees/elephant/hooks';
import { SearchTable } from '@aibees/elephant/types';
import { useGetBrowseIdsApiData } from '@aibees/shared/features/advance-table/components/table';
import { classnames } from '@aibees/shared/utils';
import { useMediaQuery } from '@mui/material';
import { memo, useMemo } from 'react';
import { SearchModality } from '../../hooks/types';
import { SearchTableActionDialogExport } from '../search-table-action-dialog-export';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableActionsProps as Props } from './types';

export const SearchTableHeaderActions = memo(
  ({ className, table, withButtonGroup, compact, activeTab }: Props) => {
    const searchTable = useSearchTableFromRoute();
    const getSelectedIdsApi = useGetBrowseIdsApiData(table);
    const { exportDialogState, saveDialogState, labels, lists } =
      useTableActionContext();

    const isSmallScreen = useMediaQuery('(max-width:1200px)');
    const isSmall = useMemo(
      () => compact || isSmallScreen,
      [compact, isSmallScreen]
    );
    const wrapperClassName = useMemo(
      () => classnames('SearchTableHeaderActions', className),
      [className]
    );

    const selectionDisabled =
      !table?.selection.count || getSelectedIdsApi?.isFetching;

    const actionButtons = useMemo(
      () => (
        <>
          {activeTab !== SearchModality.SAVED && (
            <SaveDialog
              element={
                <SearchTableHeaderActionButton
                  disabled={selectionDisabled}
                  compact={isSmall}
                  title="Save Records"
                  icon="save"
                  tooltipTitle="Saving records is not supported in AI based searches. You can however add companies to a list and save them inside the company search."
                  onClick={saveDialogState.onTrue}
                />
              }
              table={table}
            />
          )}

          <SearchTableActionDialogExport
            table={table}
            isSmall={isSmall}
            disabled={selectionDisabled}
          />

          {searchTable !== SearchTable.Employees && (
            <FindButton
              disabled={selectionDisabled}
              isSmall={isSmall}
              table={table}
            />
          )}

          <SearchTableActionDialogLists
            table={table}
            isSmall={isSmall}
            disabled={selectionDisabled}
          />

          {activeTab === SearchModality.SAVED && (
            <EnrichButton
              table={table}
              isSmall={isSmall}
              disabled={selectionDisabled}
            />
          )}

          <SearchTableActionDialogMore
            table={table}
            isSmall={isSmall}
            disabled={selectionDisabled}
          />
        </>
      ),
      [
        activeTab,
        isSmall,
        searchTable,
        selectionDisabled,
        table,
        saveDialogState,
      ]
    );

    return (
      <Styled.Wrapper
        className={wrapperClassName}
        sx={SX.root}
        direction="row"
        gap={1}
        alignItems="center"
      >
        {withButtonGroup ? (
          <Styled.ButtonWrapper variant="outlined" color="inherit">
            {actionButtons}
          </Styled.ButtonWrapper>
        ) : (
          actionButtons
        )}
      </Styled.Wrapper>
    );
  }
);

SearchTableHeaderActions.displayName = 'SearchTableHeaderActions';
